import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Player as GistPlayer } from "@gistmed/gist-ui";
import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import doctor2Img from "../../../assets/Mayo/Doctor2.png";
import MedicinesChanges from "./Instructions/MedicinesChanges";
import Transition from "./Transition";
import { postAnalytics } from "../../../../PatientComponents/analytics";
import SatisfactionQuestions from "./Instructions/SatisfactionQuestions";
import Share from "./Share";
import useVideo from "../../../hooks/useVideo";
import { usePatientFacing } from "../../../flow/patientFacing/hooks/usePatientFacing";
import GIPatientHeader from "./components/GIPatientHeader";
import { useLanguage } from "../../../providers/LanguageProvider";
import { toVideoLanguage } from "./utils/videoLanguage";

const videoThumbnail = "/thumbnail1.jpg";

// Detect mobile devices for error logging
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Clinic phone number for patient contact
const CONTACT_PHONE = "401-4218800";

export default function GIVideo() {
  const { t } = useTranslation();
  const { getEventDataLocal } = usePatientFacing();
  const { lang: selectedLanguage } = useLanguage();
  const videoLanguage = toVideoLanguage(selectedLanguage);
  const event = getEventDataLocal();

  // Debug: Log event data on mobile
  useEffect(() => {
    if (isMobile) {
      console.log('[Mobile Debug] Event:', event);
      console.log('[Mobile Debug] Case:', event?.Case);
      console.log('[Mobile Debug] Avatar:', event?.Case?.Avatar);
      console.log('[Mobile Debug] GiCase:', event?.Case?.GiCase);
    }
  }, []);

  const hasSentPostAnalytics = useRef(false);
  
  const caseId = event?.Case?.id;
  const caseIdRef = useRef(caseId);
  useEffect(() => {
    caseIdRef.current = caseId;
  }, [caseId]);
  const {
    bloodPressureMedicines,
    bloodThinners,
    diabetesMedicines,
    nutritionalSupplement,
    colonoscopyBefore,
    concentrate,
    procedureDate,
    procedureTime,
    location,
    personalNote,
    doctorName,
  } = event?.Case?.GiCase || {};
  
  // Extract weekday and dayTime from procedure data
  // Use local time to avoid UTC timezone offset issues
  // Handle both date-only strings ("2026-06-03") and ISO strings ("2026-06-03T00:00:00.000Z")
  const safeDate = procedureDate 
    ? (typeof procedureDate === 'string' 
      ? new Date(procedureDate.split('T')[0] + 'T00:00:00') 
      : new Date(procedureDate))
    : null;
  const weekday = safeDate ? safeDate.toLocaleDateString('en-US', { weekday: 'long' }) : undefined;
  const time = procedureTime ? (parseInt(procedureTime.split(':')[0]) < 17 ? 'afternoon' : 'evening') : undefined;
  
  // Build questionnaire array based on user's medical answers
  const isYes = (val) => val === true || val === "Yes";
  const questionnaire = [];
  if (isYes(bloodThinners)) questionnaire.push("bloodThinners");
  if (isYes(diabetesMedicines)) questionnaire.push("diabetesMedicines");
  if (isYes(bloodPressureMedicines)) questionnaire.push("bloodPressureMedicines");
  if (isYes(nutritionalSupplement)) questionnaire.push("nutritionalSupplement");

  const data = {
    weekday,
    time,
    concentrate: concentrate ? concentrate.split(',')[0].toLowerCase() : undefined,
    questionnaire,
    doctorName,
  };
  
  // Debug: Log data values on mobile
  useEffect(() => {
    if (isMobile) {
      console.log('[Mobile Debug] Data:', data);
      console.log('[Mobile Debug] Concentrate raw:', concentrate);
      console.log('[Mobile Debug] Weekday:', weekday);
      console.log('[Mobile Debug] Time:', time);
    }
  }, []);
  
  const { ethnicity: rawEthnicity, age, gender} = event?.Case?.Avatar || {};
  const ethnicityMap = { white: "light", black: "dark" };
  const ethnicity = ethnicityMap[rawEthnicity] || rawEthnicity;

  const [showFeedback, setShowFeedback] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const videoRef = useRef(null);
  const videoSrcRef = useRef("");

  const hasSentVideoStarted = useRef(false);

  const saveUserAction = useCallback((percentage) => {
    const currentCaseId = caseIdRef.current;
    console.log('Video progress:', { percentage, hasSentPostAnalytics: hasSentPostAnalytics.current, caseId: currentCaseId });
    if(!hasSentPostAnalytics.current && percentage >= 75) {
      hasSentPostAnalytics.current = true;
      console.log('Sending watchedVideo analytics for case:', currentCaseId);
      postAnalytics(currentCaseId, "watched-video", undefined, "gi");
    }
  }, []);

  const handleVideoStart = useCallback(() => {
    // Track when video playback starts
    const currentCaseId = caseIdRef.current;
    if (!hasSentVideoStarted.current) {
      hasSentVideoStarted.current = true;
      console.log('Video started analytics for case:', currentCaseId);
      postAnalytics(currentCaseId, "video-started", undefined, "gi");
    }
    setShowFeedback(true);
  }, []);

  const handleLoadMetadata = useCallback(() => {
    console.log('[GIVideo] Metadata loaded');
  }, []);

  const handleTimeUpdate = useCallback((location) => {
    console.log('[GIVideo] Time update:', location);
  }, []);

  const handleVideoError = useCallback((error) => {
    // Track video playback errors
    const currentCaseId = caseIdRef.current;
    console.error('Video error analytics for case:', currentCaseId, error);
    postAnalytics(currentCaseId, "video-error", { 
      errorCode: error?.code, 
      errorMessage: error?.message,
      videoSrc: videoSrcRef.current,
      isMobile: isMobile,
      userAgent: navigator.userAgent
    }, "gi");
  }, []);

  const handleAutoPlay = () => {
    if (!videoRef.current) return;
    postAnalytics(caseId, "back-to-top");
    videoRef.current.play();
  };

  // Get hospital name from localStorage and map "gi" to "UGI"
  const hospitalData = JSON.parse(localStorage.getItem("hospital") || "{}");
  const hospitalName = hospitalData.name?.toLowerCase() === "gi" ? "UGI" : "UGI";

  // Check query parameter for sending number (1 = first, 2 = second)
  // Also check event.facingType as fallback: 'gi' = sending 1, 'gi2' = sending 2
  const [searchParams] = useSearchParams();
  const querySending = searchParams.get("sending");
  const eventFacingType = event?.facingType;
  const sending = querySending || (eventFacingType === 'gi' ? '1' : '2');
  const projectName = sending === "1" ? "UGI Video 01" : "UGI Video 02";

  // TODO - add backup option if no avatar available
  const { video } = useVideo({
    language: videoLanguage,
    projectName: projectName,
    hospitalName: hospitalName,
    avatar: {
      age: age || "middle",
      gender: gender || "male",
      ethnicity: ethnicity || "light",
      hospital: "UGI",
      language: videoLanguage
    },
    data,
  });

  // Update videoSrcRef when video.src changes
  useEffect(() => {
    videoSrcRef.current = video.src || "";
  }, [video.src]);

  // Delay player rendering to prevent video.js focus error
  useEffect(() => {
    console.log('[Video Debug] video.src changed:', video.src);
    if (video.src) {
      const timer = setTimeout(() => {
        console.log('[Video Debug] Setting playerReady to true');
        setPlayerReady(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setPlayerReady(false);
    }
  }, [video.src]);

  return (
    <Transition>
      <Container>
        <Desktop>
          <GIPatientHeader padding="15px 30px" />
          <Title id="video-title">
            {t("Video-Title")}
          </Title>
          {console.log('[Video Debug] Render - video.src:', video.src, 'playerReady:', playerReady)}
          <VideoWrapper>
            {video.src && playerReady ? (
              <>
                {console.log('[Video Debug] Rendering GistPlayer with src:', video.src)}
                <GistPlayer
                  key={video.src}
                  onLocationUpdate={saveUserAction}
                  onPlayerPlaying={handleVideoStart}
                  onLoadMetadata={handleLoadMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onError={(err) => {
                    console.error('[Video Debug] GistPlayer error:', err);
                    handleVideoError(err);
                  }}
                  src={video.src}
                  thumbnail={videoThumbnail}
                  showGistmdWaterMark={false}
                  videoRef={videoRef}
                  backgroundMusic={null}
                />
              </>
            ) : video.src ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading video player...</p>
                <p style={{fontSize: '12px', color: '#666'}}>src: {video.src ? 'yes' : 'no'}, ready: {playerReady ? 'yes' : 'no'}</p>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                <p>Video not loading. Check console for details.</p>
              </div>
            )}
            
          </VideoWrapper>
          <VideoInteraction>
            <ShareWrapper>
              <Share />
            </ShareWrapper>
            <SatisfactionQuestions videoStarted={showFeedback} />
          </VideoInteraction>

          {/* Show procedure details for second sending */}
          {sending === "2" && procedureDate && (
            <ProcedureDetails>
              <ProcedureText>
                {t("Video-Procedure-Scheduled")}{" "}
                <strong>
                  {safeDate.toLocaleDateString(selectedLanguage || "en", {
                    weekday: "long",
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  })}{" "}
                  {t("Video-Procedure-At")}{" "}
                  {(() => {
                    const time = procedureTime || "16:00";
                    const [hours, minutes] = time.split(":").map(Number);
                    const date = new Date();
                    date.setHours(hours, minutes, 0, 0);
                    return date.toLocaleTimeString(selectedLanguage || "en", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    });
                  })()}
                </strong>
              </ProcedureText>
              <ProcedureText>
                {t("Video-Arrival-Time")}{" "}
                <strong>
                  {(() => {
                    const time = procedureTime || "16:00";
                    const [hours, minutes] = time.split(":").map(Number);
                    const arrival = new Date();
                    arrival.setHours(hours, minutes - 45, 0, 0);
                    return arrival.toLocaleTimeString(selectedLanguage || "en", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    });
                  })()}
                </strong>
              </ProcedureText>
              <ProcedureText>
                {t("Video-Location")}{" "}
                <strong>{location || personalNote || t("Video-Default-Location")}</strong>
              </ProcedureText>
            </ProcedureDetails>
          )}

          <MedicinesChanges />

          <DoctorImage src={doctor2Img} alt="doctor" />

          <SectionBlock>
            <SectionTitle>{t("Video-WhatNext-Title")}</SectionTitle>
            <SectionParagraph>{t("Video-WhatNext-Paragraph")}</SectionParagraph>
          </SectionBlock>

          <SectionDivider />

          <SectionBlock>
            <SectionTitle>{t("Video-Contact-Title")}</SectionTitle>
            <ContactText>
              {t("Video-Contact-Subtitle")}
              <br />
              {t("Video-Contact-Hospital")}
            </ContactText>
            <PhoneButton
              href={CONTACT_PHONE ? `tel:${CONTACT_PHONE}` : undefined}
              aria-label="Call clinic"
              onClick={() => postAnalytics(caseId, "contact-clinic-clicked")}
            >
              <PhoneIcon viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill="#ffffff"
                  d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.05-.24c1.16.39 2.4.6 3.66.6a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.46a1 1 0 0 1 1 1c0 1.27.21 2.5.6 3.66a1 1 0 0 1-.24 1.05l-2.2 2.08Z"
                />
              </PhoneIcon>
            </PhoneButton>
          </SectionBlock>

          <Footer>
            <span>{t("Footer").split(",")[0] + ","}</span>
            <span>{t("Footer").split(",").slice(1).join(",").trim()}</span>
          </Footer>
        </Desktop>
      </Container>
    </Transition>
  );
}

const Container = styled.div`
  --screen-margin: 23px;
  background: transparent
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f1f4fb 10%,
      #e3e8f6 53%,
      #f5f7fc 85%,
      #ffffff 100%
    )
    0% 0% no-repeat padding-box;
  padding-block-end: 35px;
`;

const Desktop = styled.div`
  max-width: 800px;
  margin-inline: auto;
`;

const Title = styled.h1`
  margin-block-start: 2.125rem;
  margin-block-end: 1.813rem;
  text-align: start;
  font-family: "Abraham", sans-serif;
  font-size: 1.5rem;        /* ~24px relative */
  font-weight: 600;         /* bold weight */
  margin-inline: var(--screen-margin);
`;

const VideoInteraction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 27px;
  justify-content: end;
  margin-block: 8px 30px;
  margin-inline: var(--screen-margin);
`;

const Footer = styled.footer`
  font-family: "Abraham", sans-serif;
  font-weight: bold;
  text-align: center;
  font-size: 1.5rem;
  color: #1a1a1a;
  margin-block-start: 35px;
  padding-inline: 40px;
  line-height: 1.3;
  span {
    display: block;
  }
`;

const SectionBlock = styled.div`
  margin-inline: var(--screen-margin);
  margin-block: 1.75rem;
`;

const SectionDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: #d6deef;
  margin-inline: var(--screen-margin);
  margin-block: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-family: "Abraham", sans-serif;
  font-weight: bold;
  font-size: 1.5rem;
  color: #1a1a1a;
  margin-block-start: 0;
  margin-block-end: 1rem;
`;

const SectionParagraph = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1.1875rem;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.5;
`;

const ContactText = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1.1875rem;
  color: #1a1a1a;
  margin-block: 0 1.25rem;
  line-height: 1.5;
`;

const PhoneButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #5B2FBF;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.15s ease;
  &:hover {
    transform: scale(1.05);
  }
`;

const PhoneIcon = styled.svg`
  width: 26px;
  height: 26px;
`;

const DoctorImage = styled.img`
  width: 200px;
  max-width: 100%;
  display: block;
  margin-block-end: 22px;
  margin-inline: auto;
  object-fit: contain;
`;

const ShareWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-block-start: 0;
`;


const VideoWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  width: calc(100% - 30px);
  margin-inline: auto;
  height: auto;
  display: block;
`;

const ProcedureDetails = styled.div`
  margin: 24px var(--screen-margin);
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #84a4fc;
`;

const ProcedureText = styled.p`
  font-family: "Abraham", sans-serif;
  font-size: 1rem;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  line-height: 1.5;
  &:last-child {
    margin-bottom: 0;
  }
  strong {
    font-weight: 600;
    color: #333;
  }
`;

